import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const spravatoRelated = [
  {
    title: "SAINT® vs Ketamine",
    href: "/learn/saint-vs-ketamine",
    description: "Comparing SAINT® and IV ketamine infusions",
  },
  {
    title: "SAINT® vs ECT",
    href: "/learn/saint-vs-ect",
    description: "Comparing SAINT® and electroconvulsive therapy",
  },
  {
    title: "What if SAINT® doesn't work?",
    href: "/learn/what-if-saint-doesnt-work",
    description: "Next steps after SAINT® treatment",
  },
  {
    title: "SAINT® TMS Cost",
    href: "/learn/saint-tms-cost",
    description: "Pricing, insurance and financing for SAINT®",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "Which is more effective, Spravato or SAINT?",
    answer:
      "Neither is universally more effective. In a 2022 randomized trial, 79% of participants receiving SAINT® reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Spravato has separate FDA trial data for its own indications. The right choice depends on your diagnosis, history, and whether you prefer a device-based or medication-based approach. A psychiatric evaluation determines which may be appropriate.",
  },
  {
    question: "Can I do both SAINT and Spravato?",
    answer:
      "Yes, in some cases. NWCOA offers both SAINT® and SPRAVATO® in the same clinic, so your psychiatrist can coordinate care if combining or sequencing them makes clinical sense. Some patients use Spravato as a follow-on or adjunct after SAINT®, or vice versa. Whether combining is appropriate depends on your individual circumstances and should be discussed with your provider.",
  },
  {
    question: "Which lasts longer, SAINT or Spravato?",
    answer:
      "SAINT® is delivered over five days, and follow-up data from the 2022 trial tracked outcomes over several weeks. Spravato is an ongoing treatment — typically twice weekly for four weeks, then maintenance doses. Durability varies by individual. Neither is a one-time fix; both may require follow-up or maintenance. Your psychiatrist can discuss what follow-up looks like for each.",
  },
  {
    question: "What is the SPRAVATO REMS program?",
    answer:
      "SPRAVATO® is available only through a Risk Evaluation and Mitigation Strategy (REMS) program because of the risks of sedation, dissociation, and blood pressure changes. Each dose is self-administered as a nasal spray at a certified healthcare setting under supervision, followed by at least two hours of monitoring. Patients cannot drive after a session. NWCOA is a REMS-certified site.",
  },
  {
    question: "Does SAINT require monitoring after each session?",
    answer:
      "No. SAINT® sessions are delivered while you sit awake in a chair, and there is no post-session monitoring period — you can generally return to your day. SAINT® does require an MRI scan before treatment to personalize the target. Spravato, by contrast, requires at least two hours of monitoring after each dose.",
  },
  {
    question: "Does insurance cover SAINT or Spravato?",
    answer:
      "SPRAVATO® may be covered by insurance for its FDA-approved indications, subject to prior authorization and plan terms. SAINT® uses Category III CPT codes and is typically self-pay, starting at $25,000 at NWCOA, with financing available. Coverage varies by plan for both. We verify your benefits for free — call 954-827-9793.",
  },
];

export const pageSaintVsSpravato = makeArticle({
  slug: "saint-vs-spravato",
  title: "SAINT® TMS vs SPRAVATO®: Understanding the Differences",
  description:
    "SAINT® vs Spravato compared: brain stimulation vs medication, schedule, monitoring, REMS, cost, insurance, and which lasts longer. Neither is universally recommended.",
  canonical: "/learn/saint-vs-spravato",
  h1: "SAINT® TMS vs SPRAVATO®: Understanding the Differences",
  breadcrumbLabel: "SAINT® vs Spravato",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) and SPRAVATO® (esketamine) are both treatments for treatment-resistant depression, but they work very differently. SAINT® is a non-invasive brain stimulation device treatment delivered over five days. SPRAVATO® is an FDA-approved medication (esketamine nasal spray) administered in a REMS-certified clinic, typically twice weekly for four weeks. Neither is universally recommended — the right option depends on your individual circumstances and should be discussed with a psychiatrist.",
  keyTakeaways: [
    "SAINT® is non-invasive brain stimulation; SPRAVATO® is a medication (esketamine).",
    "SAINT® is delivered in 5 consecutive days; SPRAVATO® is twice weekly for 4 weeks.",
    "SPRAVATO® causes dissociation and requires 2-hour post-dose monitoring under the REMS program.",
    "SAINT® requires an MRI before treatment; SPRAVATO® does not.",
    "Neither is universally recommended — the choice depends on clinical circumstances.",
  ],
  sections: [
    {
      heading: "Treatment Type: Brain Stimulation vs Medication",
      paragraphs: [
        "SAINT® is a non-invasive brain stimulation device treatment. It uses magnetic pulses — delivered through a coil placed on the scalp — to stimulate a specific brain region linked to mood. No drug enters the body. The target is personalized using an fMRI scan taken before treatment begins. SAINT® is FDA-cleared for adults with major depressive disorder (MDD) whose depression hasn't improved enough on prior antidepressant medication.",
        "SPRAVATO® (esketamine) is a prescription medication — a nasal spray form of esketamine, a derivative of ketamine. It is FDA-approved as an adjunct (add-on) to an oral antidepressant for adults with treatment-resistant depression and for depressive symptoms in adults with major depressive disorder with acute suicidal ideation or behavior. It is self-administered under supervision at a certified healthcare setting enrolled in the SPRAVATO REMS program.",
      ],
    },
    {
      heading: "How Each Is Administered",
      paragraphs: [
        "SAINT® is delivered while you sit awake in a chair. A coil on the scalp delivers magnetic pulses — ten sessions per day over five consecutive days, with about 50-minute breaks between sessions. No anesthesia or sedation is used. You can generally drive yourself to and from the clinic.",
        "SPRAVATO® is self-administered as a nasal spray at the clinic under the supervision of a healthcare provider. Because it can cause sedation and dissociation, you remain at the clinic for at least two hours of monitoring after each dose, and you cannot drive until the next day. A driver is required for each SPRAVATO® appointment.",
      ],
    },
    {
      heading: "Treatment Schedule",
      paragraphs: [
        "SAINT® compresses the entire treatment into one week: 50 sessions over five consecutive days. After the week, there is no ongoing in-clinic treatment schedule, though follow-up visits help track your response.",
        "SPRAVATO® is an ongoing treatment. The typical induction phase is twice weekly for four weeks (eight sessions), followed by a maintenance phase with less frequent doses. The full course spans weeks to months, with continued clinic visits.",
      ],
    },
    {
      heading: "Monitoring and the SPRAVATO REMS Program",
      paragraphs: [
        "SPRAVATO® carries a Risk Evaluation and Mitigation Strategy (REMS) because of the risks of sedation, dissociation, and blood pressure changes. Each dose must be administered at a REMS-certified healthcare setting, followed by at least two hours of monitoring. Patients cannot drive after a session. NWCOA is a REMS-certified site.",
        "SAINT® requires no post-session monitoring — you return home after each day. However, SAINT® does require an MRI scan before treatment to personalize the brain target, so people who cannot have an MRI are not candidates for SAINT®.",
      ],
    },
    {
      heading: "Side Effects",
      paragraphs: [
        "SAINT®'s most commonly reported side effects are headache and scalp discomfort, which usually fade within days. TMS carries a small risk of seizure.",
        "SPRAVATO®'s common side effects include dissociation, dizziness, nausea, sedation, and increased blood pressure. Because of these effects, monitoring is required after each dose.",
      ],
    },
    {
      heading: "Eligibility",
      paragraphs: [
        "Both are options for adults with treatment-resistant depression. SAINT® requires MRI safety screening — people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure are not candidates. SPRAVATO® has its own contraindications, including aneurysmal vascular disease. A psychiatric evaluation determines which may be appropriate for you. NWCOA treats adults 22 and older.",
      ],
    },
    {
      heading: "Durability: How Long Each Treatment Lasts",
      paragraphs: [
        "Durability — how long the improvement lasts — is a key question for both treatments. In the 2022 SAINT® randomized trial, 79% of participants reached remission, with outcomes tracked over several weeks of follow-up (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary, and some people may need additional or maintenance treatment.",
        "SPRAVATO® is designed as an ongoing treatment with a maintenance phase, reflecting that its effects are sustained through continued dosing. The maintenance schedule is adjusted based on your response. Neither treatment is a one-time fix; both may require follow-up. Your psychiatrist can discuss what follow-up looks like for each and what to expect based on your situation.",
      ],
    },
    {
      heading: "Cost and Insurance",
      paragraphs: [
        "SAINT® at NWCOA starts at $25,000. It uses Category III CPT codes and is typically self-pay, though coverage may be available depending on your plan. We verify your benefits for free and offer financing on a case-by-case basis, discussed during your consultation.",
        "SPRAVATO® may be covered by insurance for its FDA-approved indications, subject to prior authorization and plan terms. Coverage varies by plan for both treatments. Neuro Wellness verifies benefits for both. Learn more about [[insurance coverage for SAINT®|/learn/does-insurance-cover-saint-tms]] and [[financing options|/contents/patient-info/financing-options]].",
      ],
    },
    {
      heading: "Can I Combine SAINT® and SPRAVATO®?",
      paragraphs: [
        "Yes, in some cases. NWCOA offers both SAINT® and SPRAVATO® in the same clinic, so your psychiatrist can coordinate care if combining or sequencing the two makes clinical sense for you. Some patients use one as a follow-on after the other, or as an adjunct. Whether combining is appropriate depends on your diagnosis, history, and response — and should be discussed with your provider.",
        "Because both are available under one roof, you don't have to choose between clinics. Your care team can help you decide which to start with and whether adding the other later makes sense. [[See if SAINT® is a fit|/am-i-a-candidate-screener]] or [[check your insurance|/check-insurance-options]].",
      ],
    },
    {
      heading: "SAINT® vs SPRAVATO® Side-by-Side Comparison",
      table: {
        headers: ["Feature", "SAINT®", "SPRAVATO®"],
        rows: [
          [
            "Treatment type",
            "Non-invasive brain stimulation (device)",
            "Medication (esketamine nasal spray)",
          ],
          [
            "FDA status",
            "FDA-cleared (K220177) for MDD after medication",
            "FDA-approved (adjunct for TRD; acute suicidal ideation)",
          ],
          ["Sessions", "50 total", "8 induction + maintenance"],
          [
            "Schedule",
            "10 sessions/day × 5 days",
            "Twice weekly × 4 weeks, then maintenance",
          ],
          [
            "Time to response",
            "Within the 5-day week",
            "Weeks (induction phase)",
          ],
          [
            "Anesthesia",
            "None — awake",
            "None, but sedation/dissociation occurs",
          ],
          ["Post-dose monitoring", "None", "≥2 hours (REMS required)"],
          ["Driving after session", "Generally yes", "No — driver required"],
          ["MRI required", "Yes (for targeting)", "No"],
          [
            "Common side effects",
            "Headache, scalp discomfort",
            "Dissociation, dizziness, nausea, BP changes",
          ],
          [
            "Cost at NWCOA",
            "Starting at $25,000",
            "May be covered; varies by plan",
          ],
          [
            "Insurance pathway",
            "Category III CPT; typically self-pay",
            "Prior authorization; may be covered",
          ],
        ],
      },
    },
    {
      heading: "Who Each Treatment Fits",
      paragraphs: [
        "SAINT® may fit adults with major depressive disorder who haven't improved enough on antidepressant medication and who prefer a non-invasive, device-based, one-week treatment. It requires an MRI and five full days at the clinic.",
        "SPRAVATO® may fit adults with treatment-resistant depression (as an adjunct to an oral antidepressant) or those with acute suicidal ideation. It requires ongoing clinic visits, a driver for each session, and REMS-certified monitoring.",
        "Neither is universally recommended. The right choice depends on your diagnosis, history, side-effect tolerance, schedule, and insurance. A psychiatric evaluation at NWCOA can help determine which — or whether a combination — is appropriate. Learn more about [[treatment-resistant depression|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. SPRAVATO (esketamine) prescribing information.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: spravatoRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "October 2026",
    lastReviewed: "October 2026",
    lastUpdated: "October 2026",
  },
  toc: [
    {
      label: "Treatment Type",
      href: "#treatment-type-brain-stimulation-vs-medication",
    },
    { label: "How Each Is Administered", href: "#how-each-is-administered" },
    { label: "Treatment Schedule", href: "#treatment-schedule" },
    {
      label: "Monitoring & REMS",
      href: "#monitoring-and-the-spravato-rems-program",
    },
    { label: "Side Effects", href: "#side-effects" },
    { label: "Eligibility", href: "#eligibility" },
    { label: "Durability", href: "#durability-how-long-each-treatment-lasts" },
    { label: "Cost & Insurance", href: "#cost-and-insurance" },
    { label: "Can I Combine Them?", href: "#can-i-combine-saint-and-spravato" },
    {
      label: "Comparison Table",
      href: "#saint-vs-spravato-side-by-side-comparison",
    },
    { label: "Who Each Fits", href: "#who-each-treatment-fits" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
